import { useState } from 'react'
import {
  Container,
  Typography,
  Button,
  Box,
  Stack,
  Alert,
  AlertTitle,
  Divider,
} from '@mui/material'
import { Add } from '@mui/icons-material'
import { useFamilyStore } from '@/store/useFamilyStore'
import { useAuthStore } from '@/store/useAuthStore'
import FamilyMemberCard from '@/components/family/FamilyMemberCard'
import FamilyMemberForm from '@/components/family/FamilyMemberForm'
import NotificationSettings from '@/components/notifications/NotificationSettings'
import type { FamilyMember } from '@/types'

export default function SettingsPage() {
  const [formOpen, setFormOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null)

  const members = useFamilyStore((state) => state.members)
  const deleteMember = useFamilyStore((state) => state.deleteMember)
  const currentUserId = useAuthStore((state) => state.currentUserId)
  const setCurrentUser = useAuthStore((state) => state.setCurrentUser)

  const handleEdit = (member: FamilyMember) => {
    setEditingMember(member)
    setFormOpen(true)
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this family member?')) {
      await deleteMember(id)
      // If deleted member was current user, clear selection
      if (currentUserId === id) {
        setCurrentUser('')
      }
    }
  }

  const handleCloseForm = () => {
    setFormOpen(false)
    setEditingMember(null)
  }

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage family members and app preferences
        </Typography>
      </Box>

      {/* User Selection Alert */}
      {members.length > 0 && !currentUserId && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Select Your User</AlertTitle>
          Click on any family member card below to select who you are. This will unlock all app features!
        </Alert>
      )}

      {/* Family Members Section */}
      <Box mb={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5">Family Members</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setFormOpen(true)}
          >
            Add Member
          </Button>
        </Box>

        {members.length === 0 && (
          <Alert severity="info">
            <AlertTitle>No Family Members Yet</AlertTitle>
            Add your first family member to get started with task management!
          </Alert>
        )}

        <Stack spacing={2}>
          {members.map((member) => (
            <Box
              key={member.id}
              onClick={() => !currentUserId && setCurrentUser(member.id)}
              sx={{
                cursor: !currentUserId ? 'pointer' : 'default',
                '&:hover': !currentUserId ? {
                  transform: 'scale(1.02)',
                  transition: 'transform 0.2s',
                } : {},
              }}
            >
              <FamilyMemberCard
                member={member}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Notification Settings Section */}
      {currentUserId && (
        <Box mb={4}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Notifications
          </Typography>
          <NotificationSettings />
        </Box>
      )}

      <Divider sx={{ my: 4 }} />

      {/* App Settings Section */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom>
          App Settings
        </Typography>
        <Alert severity="info">
          <AlertTitle>PIN Configuration</AlertTitle>
          To change your PIN, update the VITE_APP_PIN environment variable and
          redeploy the app.
        </Alert>
      </Box>

      {/* Family Member Form Dialog */}
      <FamilyMemberForm
        open={formOpen}
        onClose={handleCloseForm}
        member={editingMember}
      />
    </Container>
  )
}
